import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, Star, Plus, Sprout } from "lucide-react";
import { PageHeader, HeritageSeal } from "@/components/primitives";
import { BrandLogo } from "@/components/BrandLogo";
import { listPlants, updatePlant } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const FILTERS = [
  { key: "favourites", label: "Favourites", icon: Star },
  { key: "all", label: "All" },
  { key: "purchased", label: "Purchased", dot: "#7E8C5B" },
  { key: "planted", label: "Planted", dot: "#A4543A" },
  { key: "germinated", label: "Germinated", dot: "#5F6B43" },
  { key: "flowering", label: "Flowering", dot: "#C26B4A" },
  { key: "dormant", label: "Dormant", dot: "#8A8474" },
];

const STAGE_COLOR = {
  purchased: "#7E8C5B",
  planted: "#A4543A",
  germinated: "#5F6B43",
  flowering: "#C26B4A",
  dormant: "#8A8474",
  finished: "#5A5448",
};

export default function Garden() {
  const { user } = useAuth();
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("date-new");
  const firstName = (user?.name || "").split(" ")[0] || "Gardener";
  const gardenName = user?.garden_name;

  const load = async () => {
    setLoading(true);
    try {
      const data = await listPlants();
      setPlants(data);
    } catch (e) {
      toast.error("Could not fetch your patch");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = [...plants];
    if (filter === "favourites") list = list.filter((p) => p.favorite);
    else if (filter !== "all") list = list.filter((p) => p.stage === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((p) =>
        [p.name, p.variety, p.vendor, p.color].filter(Boolean).some((s) => s.toLowerCase().includes(q))
      );
    }
    // Sort
    const safe = (s) => (s || "").toString().toLowerCase();
    switch (sortBy) {
      case "alpha":
        list.sort((a, b) => safe(a.name).localeCompare(safe(b.name)));
        break;
      case "alpha-desc":
        list.sort((a, b) => safe(b.name).localeCompare(safe(a.name)));
        break;
      case "date-new":
        list.sort((a, b) => safe(b.created_at).localeCompare(safe(a.created_at)));
        break;
      case "date-old":
        list.sort((a, b) => safe(a.created_at).localeCompare(safe(b.created_at)));
        break;
      case "colour":
        list.sort((a, b) => safe(a.color).localeCompare(safe(b.color)) || safe(a.name).localeCompare(safe(b.name)));
        break;
      case "type":
        list.sort((a, b) =>
          safe(a.category).localeCompare(safe(b.category))
          || safe(a.type).localeCompare(safe(b.type))
          || safe(a.name).localeCompare(safe(b.name)));
        break;
      case "height-tall":
        list.sort((a, b) => (Number(b.height_cm) || 0) - (Number(a.height_cm) || 0));
        break;
      case "height-short":
        list.sort((a, b) => (Number(a.height_cm) || 9999) - (Number(b.height_cm) || 9999));
        break;
      default: break;
    }
    return list;
  }, [plants, filter, query, sortBy]);

  const toggleFav = async (p) => {
    try {
      const updated = await updatePlant(p.id, { favorite: !p.favorite });
      setPlants((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
    } catch (e) {
      toast.error("Could not update");
    }
  };

  return (
    <div data-testid="garden-page">
      <PageHeader
        kicker={`Welcome, ${firstName}`}
        title={gardenName || "My Garden"}
        subtitle={`${plants.length} ${plants.length === 1 ? "plant" : "plants"} cultivated`}
      />

      {/* Search + Sort */}
      <div className="mt-4 flex flex-col sm:flex-row gap-2 items-stretch" data-testid="garden-search-wrap">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
          <input
            type="text"
            placeholder="Search plants, varieties, vendors..."
            className="field pl-10"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="garden-search-input"
          />
        </div>
        <label className="flex items-center gap-2 sm:w-72" data-testid="garden-sort-wrap">
          <span className="text-xs text-ink-soft whitespace-nowrap font-display italic">Sort by</span>
          <select
            className="field"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="garden-sort"
          >
            <option value="date-new">Date entered (newest)</option>
            <option value="date-old">Date entered (oldest)</option>
            <option value="alpha">Alphabetical (A → Z)</option>
            <option value="alpha-desc">Alphabetical (Z → A)</option>
            <option value="colour">Colour</option>
            <option value="type">Flower / plant type</option>
            <option value="height-tall">Height (tallest first)</option>
            <option value="height-short">Height (shortest first)</option>
          </select>
        </label>
      </div>

      {/* Filter chips */}
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="garden-filter-chips">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          return (
            <button
              key={f.key}
              className="chip whitespace-nowrap"
              data-active={filter === f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
            >
              {Icon && <Icon size={13} className={filter === f.key ? "" : "text-terracotta"} fill={filter === f.key ? "currentColor" : "none"} />}
              {f.dot && (
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: f.dot }} />
              )}
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-center mt-16 text-ink-soft font-display italic" data-testid="garden-loading">Gathering your patch...</div>
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="garden-grid">
          {filtered.map((p) => (
            <PlantCard key={p.id} plant={p} onToggleFav={() => toggleFav(p)} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mt-10 text-center animate-fade-up" data-testid="garden-empty-state">
      <div className="flex justify-center mb-6">
        <BrandLogo size={120} />
      </div>
      <h2 className="font-display text-3xl sm:text-4xl text-sage-dark">Your garden awaits</h2>
      <p className="mt-3 text-ink-soft max-w-md mx-auto">
        Start by adding your first plant, a seed, tuber, or cutting.
      </p>
      <Link to="/garden/new" className="btn-heritage mt-6 inline-flex" data-testid="garden-empty-cta">
        <Plus size={18} />
        Add Your First Plant
      </Link>
    </div>
  );
}

function PlantCard({ plant, onToggleFav }) {
  return (
    <Link
      to={`/garden/${plant.id}`}
      className="h-card p-4 flex gap-4 items-start hover:shadow-seal transition-shadow animate-fade-up"
      data-testid={`plant-card-${plant.id}`}
    >
      <div
        className="shrink-0 w-16 h-16 rounded-xl flex items-center justify-center"
        style={{ background: plant.photos?.[0] ? "none" : "#F0E9D2" }}
      >
        {plant.photos?.[0] ? (
          <img src={plant.photos[0]} alt={plant.name} className="w-16 h-16 rounded-xl object-cover" />
        ) : (
          <Sprout size={26} className="text-sage" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-2xl text-sage-dark truncate">{plant.name}</h3>
          <button
            onClick={(e) => { e.preventDefault(); onToggleFav(); }}
            className="text-terracotta"
            aria-label="Toggle favourite"
            data-testid={`plant-fav-${plant.id}`}
          >
            <Star size={18} fill={plant.favorite ? "currentColor" : "none"} />
          </button>
        </div>
        {plant.variety && (
          <p className="font-display italic text-ink-soft text-base -mt-1">{plant.variety}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 chip py-1 px-2.5 text-xs">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: STAGE_COLOR[plant.stage] || "#7E8C5B" }} />
            {plant.stage}
          </span>
          {plant.quantity > 1 && (
            <span className="chip py-1 px-2.5 text-xs" data-testid={`plant-qty-${plant.id}`}>×{plant.quantity}</span>
          )}
          {plant.color && <span className="text-ink-soft">· {plant.color}</span>}
          {plant.height_cm > 0 && <span className="text-ink-soft">· {plant.height_cm}cm</span>}
          {plant.vendor && <span className="text-ink-soft truncate">· {plant.vendor}</span>}
        </div>
      </div>
    </Link>
  );
}
